import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { RestfulEndpoint } from '../restful-endpoint.service';
import {
  PPE_MANUAL_RUN_LIMIT,
  PPE_STATUS_BATCH_LIMIT,
  PpeApiResponse,
  PpeManualRunResult,
  PpeIncorrectReason,
  PpeReportIncorrectRequest,
  PpeReportIncorrectResult,
  ProjectPpeManualRunState,
  ProjectPpeState,
  RemoveAllResult,
  ReportPpeDetail,
  ReportPpeStatusItem,
  RerunResult,
} from './ppe.models';

/**
 * PPE auto-detection calls (V2CORE-2901). Uses the same `v2/...` HOME routes as
 * the V2 frontend; every method returns the unwrapped `data` of the response.
 * Errors are passed through untouched so callers can read `refusalCode()`.
 */
@Injectable({
  providedIn: 'root',
})
export class PpeService {
  constructor(private endPoint: RestfulEndpoint) {}

  /** GET /api/v2/projects/{id}/ppe-detection (403 = caller may not see the switch). */
  getProjectPpe(projectId: string | number): Observable<ProjectPpeState> {
    return this.endPoint
      .get(`${projectId}/ppe-detection`, null, 'projects')
      .pipe(map((res: PpeApiResponse<ProjectPpeState>) => res?.data));
  }

  /** PUT /api/v2/projects/{id}/ppe-detection `{enabled}`. */
  setProjectPpe(projectId: string | number, enabled: boolean): Observable<ProjectPpeState> {
    return this.endPoint
      .put(`${projectId}/ppe-detection`, { enabled }, null, 'projects')
      .pipe(map((res: PpeApiResponse<ProjectPpeState>) => res?.data));
  }

  /**
   * POST /api/v2/reports/ppe-detection-status, split into calls of at most 100 ids.
   * Duplicate and empty ids are dropped; no ids means no call.
   */
  batchStatus(reportIds: string[]): Observable<ReportPpeStatusItem[]> {
    const ids = Array.from(new Set((reportIds || []).filter((id) => !!id)));
    if (!ids.length) {
      return of([]);
    }
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += PPE_STATUS_BATCH_LIMIT) {
      chunks.push(ids.slice(i, i + PPE_STATUS_BATCH_LIMIT));
    }
    return forkJoin(
      chunks.map((chunk) =>
        this.endPoint
          .post('ppe-detection-status', { report_ids: chunk }, null, 'reports')
          .pipe(map((res: PpeApiResponse<ReportPpeStatusItem[]>) => res?.data || [])),
      ),
    ).pipe(map((parts) => ([] as ReportPpeStatusItem[]).concat(...parts)));
  }

  /** GET /api/v2/projects/{id}/ppe-detection/manual-run (V2CORE-3363). */
  getManualRunState(projectId: string | number): Observable<ProjectPpeManualRunState> {
    return this.endPoint
      .get(`${projectId}/ppe-detection/manual-run`, null, 'projects')
      .pipe(map((res: PpeApiResponse<ProjectPpeManualRunState>) => res?.data));
  }

  /**
   * POST /api/v2/projects/{id}/ppe-detection/manual-run `{report_ids}` (V2CORE-3363): run PPE
   * detection on several captures of the project. Duplicate and empty ids are dropped; more than
   * 100 ids are sent in several calls whose answers are merged. No ids means no call.
   */
  manualRun(projectId: string | number, reportIds: string[]): Observable<PpeManualRunResult> {
    const ids = Array.from(new Set((reportIds || []).filter((id) => !!id)));
    if (!ids.length) {
      return of({
        project_id: String(projectId),
        queued: [],
        skipped: [],
        queued_count: 0,
        skipped_count: 0,
      });
    }
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += PPE_MANUAL_RUN_LIMIT) {
      chunks.push(ids.slice(i, i + PPE_MANUAL_RUN_LIMIT));
    }
    return forkJoin(
      chunks.map((chunk) =>
        this.endPoint
          .post(`${projectId}/ppe-detection/manual-run`, { report_ids: chunk }, null, 'projects')
          .pipe(map((res: PpeApiResponse<PpeManualRunResult>) => res?.data)),
      ),
    ).pipe(
      map((parts) => {
        const queued = ([] as PpeManualRunResult['queued']).concat(
          ...parts.map((part) => part?.queued || []),
        );
        const skipped = ([] as PpeManualRunResult['skipped']).concat(
          ...parts.map((part) => part?.skipped || []),
        );
        return {
          project_id: parts[0]?.project_id ?? String(projectId),
          queued,
          skipped,
          queued_count: queued.length,
          skipped_count: skipped.length,
        };
      }),
    );
  }

  /** GET /api/v2/reports/{report}/ppe-detection. */
  getReportPpe(reportId: string): Observable<ReportPpeDetail> {
    return this.endPoint
      .get(`${reportId}/ppe-detection`, null, 'reports')
      .pipe(map((res: PpeApiResponse<ReportPpeDetail>) => res?.data));
  }

  /** POST /api/v2/reports/{report}/ppe-detection/rerun; omit floor ids for every eligible floor. */
  rerun(reportId: string, reportFloorIds?: string[]): Observable<RerunResult> {
    const body = reportFloorIds?.length ? { report_floor_ids: reportFloorIds } : {};
    return this.endPoint
      .post(`${reportId}/ppe-detection/rerun`, body, null, 'reports')
      .pipe(map((res: PpeApiResponse<RerunResult>) => res?.data));
  }

  /** POST /api/v2/reports/{report}/ppe-detection/remove-all. */
  removeAll(reportId: string): Observable<RemoveAllResult> {
    return this.endPoint
      .post(`${reportId}/ppe-detection/remove-all`, {}, null, 'reports')
      .pipe(map((res: PpeApiResponse<RemoveAllResult>) => res?.data));
  }

  /**
   * POST /api/v2/observations/{id}/detection-feedback (V2CORE-3078): report an AI
   * observation as incorrect. The justification is trimmed and only sent when not empty.
   */
  reportIncorrect(
    observationId: string | number,
    reasonCode: PpeIncorrectReason,
    justification?: string | null,
  ): Observable<PpeReportIncorrectResult> {
    const body: PpeReportIncorrectRequest = { reason_code: reasonCode };
    const text = (justification || '').trim();
    if (text) {
      body.justification = text;
    }
    return this.endPoint
      .post(`${observationId}/detection-feedback`, body, null, 'observations')
      .pipe(map((res: PpeApiResponse<PpeReportIncorrectResult>) => res?.data));
  }

  /** The `error_code` of a failed PPE call, or null when the body has none. */
  refusalCode(err: HttpErrorResponse | unknown): string | null {
    const body = (err as HttpErrorResponse)?.error;
    const code =
      body && typeof body === 'object' ? (body as { error_code?: unknown }).error_code : null;
    return typeof code === 'string' && code ? code : null;
  }
}
